import { useMemo } from "react";
import { Link, Outlet, createFileRoute } from "@tanstack/react-router";
import { Tab, TabList } from "~/components/ui/tabs";
import { Feature, hasFeature } from "~/lib/has-feature";
import { PlanBadge } from "~/components/plan-badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { type CoachSelect, MembershipPlan } from "~/lib/schemas";
import { ArrowUpCircle, Home } from "lucide-react";
import { Button } from "~/components/ui/button";
import { OnboardingMini } from "~/components/ui/onboarding-mini";
import {
  type OnboardingStatus,
  calculateOnboardingStatus,
} from "~/lib/onboarding";
import { useCoachPrivateProfileRequired } from "~/contexts/coach";

interface ExtendedOnboardingStatus extends OnboardingStatus {
  profile_manually_edited: boolean;
  onboarding_completed_at: Date | null;
}

export const Route = createFileRoute("/coach/_backend/edit-profile")({
  head: () => ({
    meta: [
      {
        title: "Edit Profile",
      },
    ],
  }),
  component: EditProfileLayout,
});

const ProfileTabs = (props: { coach: CoachSelect }) => {
  return (
    <TabList className="w-full">
      <Tab to="/coach/edit-profile/personal-info">Personal Info</Tab>
      <Tab to="/coach/edit-profile/profile">Profile</Tab>
      <Tab to="/coach/edit-profile/offer">Main Offer</Tab>
      <Tab to="/coach/edit-profile/offers">Other Offers</Tab>
      <Tab to="/coach/edit-profile/qualifications">Qualifications</Tab>
      <Tab to="/coach/edit-profile/details">Matching Details</Tab>
      {hasFeature(props.coach, Feature.Resources) && (
        <Tab to="/coach/edit-profile/resources">Resources</Tab>
      )}
      <Tab to="/coach/edit-profile/client-flow">Client Flow</Tab>
    </TabList>
  );
};

function EditProfileLayout() {
  const coachWithRelations = useCoachPrivateProfileRequired();

  const coach = coachWithRelations;
  const onboarding: ExtendedOnboardingStatus = useMemo(
    () => ({
      ...calculateOnboardingStatus(
        {
          ...coachWithRelations,
          expertises: [], // TODO: Add expertises to CoachPrivateData type if needed
        },
        coachWithRelations.profile,
        coachWithRelations.details,
        coachWithRelations.mainOffer,
      ),
      profile_manually_edited:
        coachWithRelations.profile_manually_edited || false,
      onboarding_completed_at: coachWithRelations.onboarding_completed_at,
    }),
    [coachWithRelations],
  );

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8 flex items-center">
        <div className="flex grow flex-col">
          <div className="flex items-center justify-between">
            <Link
              to="/coach/edit-profile"
              className="-ml-4 flex items-center gap-2 rounded-lg p-2 hover:bg-gray-50 active:bg-gray-100"
            >
              <Button size="icon" variant="ghost">
                <Home />
              </Button>
              <h1 className="font-heading text-3xl leading-none">
                Edit Profile
              </h1>
            </Link>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link to="/coach/membership-plans">
                  <PlanBadge membershipPlan={coach.membershipPlan}>
                    {coach.membershipPlan === MembershipPlan.Standard && (
                      <span className="bg-gradient-brand border-brand-700 ml-2 flex items-center gap-1 rounded-full border px-2 py-1 text-xs text-white">
                        <ArrowUpCircle />
                        Upgrade
                      </span>
                    )}
                  </PlanBadge>
                </Link>
              </TooltipTrigger>
              <TooltipContent>
                Click to view or change your membership plan
              </TooltipContent>
            </Tooltip>
          </div>
          {!onboarding.onboarding_completed_at && (
            <OnboardingMini status={onboarding} />
          )}
          <ProfileTabs coach={coach} />
        </div>
      </div>
      <Outlet />
    </div>
  );
}
